import { addDays, addHours, format, parse, subDays, subHours } from 'date-fns';

/** Default logical day start. 4am is late enough that a session is rarely in flight. */
export const DEFAULT_DAY_START_HOUR = 4;

/**
 * The logical day an instant belongs to, as a `yyyy-MM-dd` key.
 *
 * This is the single answer to "which day does this belong to?". An entry's home day is a pure
 * function of its own timestamp, never of application state — that is what makes a stale active-day
 * pointer harmless.
 */
export function dayKeyFor(instant: Date | string | number, dayStartHour: number): string {
  return format(subHours(new Date(instant), dayStartHour), 'yyyy-MM-dd');
}

/**
 * The exclusive end instant of a logical day — i.e. the moment the next one begins.
 *
 * Anchors on the NEXT key's own local midnight (re-parsed from its `yyyy-MM-dd` string) rather
 * than adding `dayStartHour` hours to `addDays(parse(dayKey), 1)`. That arithmetic silently breaks
 * once a year in midnight-DST-transition zones (e.g. America/Santiago, Cuba, Lebanon): when local
 * midnight for `dayKey` itself doesn't exist (the clock jumps straight from 23:59:59 to 01:00:00),
 * `parse(dayKey, ...)` returns 01:00 instead of 00:00, and `addDays` (calendar-day arithmetic that
 * only touches the date field) carries that hour corruption forward, while `addHours` (a fixed
 * millisecond duration) compounds it — landing the boundary a full hour later than intended.
 * Formatting `addDays(start, 1)` back down to a plain date string and re-parsing it recomputes the
 * NEXT day's midnight directly, which is not itself affected by whatever corrupted `dayKey`'s own
 * midnight (the skip only affects the transition day). `format` only reads the date field, so it is
 * unaffected by an hour-shifted `start`.
 */
export function boundaryFor(dayKey: string, dayStartHour: number): Date {
  const start = parse(dayKey, 'yyyy-MM-dd', new Date());
  const nextDayKey = format(addDays(start, 1), 'yyyy-MM-dd');
  const nextMidnight = parse(nextDayKey, 'yyyy-MM-dd', new Date());
  return addHours(nextMidnight, dayStartHour);
}

/**
 * The inclusive start instant of a logical day — the complement of `boundaryFor` (that function's
 * exclusive end).
 *
 * Deliberately does NOT compute this as `dayKey`'s own local midnight + `dayStartHour` — that is
 * exactly the calculation `boundaryFor` avoids doing for its input key, for the same DST reason
 * documented there: on a spring-forward transition day, `dayKey`'s own local midnight may not
 * exist, so `parse` would silently return 01:00 instead of 00:00 and corrupt the result. Instead
 * this re-derives from the PREVIOUS key's own `boundaryFor` — by definition the end of the
 * previous logical day is the start of this one — which sidesteps the problem the same way
 * `boundaryFor` does: `format` only reads the date field when computing `previousDayKey`, so it is
 * unaffected by an hour-shifted intermediate `parse` result.
 */
export function startBoundaryFor(dayKey: string, dayStartHour: number): Date {
  const parsed = parse(dayKey, 'yyyy-MM-dd', new Date());
  const previousDayKey = format(subDays(parsed, 1), 'yyyy-MM-dd');
  return boundaryFor(previousDayKey, dayStartHour);
}

/** True when the instant falls in the logical day that is currently in progress. */
export function isCurrentLogicalDay(
  instant: Date | string | number,
  dayStartHour: number,
): boolean {
  return dayKeyFor(instant, dayStartHour) === dayKeyFor(new Date(), dayStartHour);
}
